import { hexValue } from 'ethers/lib/utils';
import zipObject from 'lodash/zipObject';
import { infuraKey, isDev } from './config';

export interface EthereumChainParameter {
  chainId: string; // A 0x-prefixed hexadecimal string
  chainName: string;
  nativeCurrency: {
    name: string;
    symbol: string; // 2-6 characters long
    decimals: number;
  };
  rpcUrls: string[];
  blockExplorerUrls?: string[];
  iconUrls?: string[]; // Currently ignored.
}

export const POLYGON_MAINNET = {
  chainId: hexValue(137),
  chainName: 'Polygon Mainnet',
  rpcUrls: ['https://polygon-rpc.com'],
  nativeCurrency: {
    name: 'Matic',
    symbol: 'MATIC',
    decimals: 18,
  },
  blockExplorerUrls: ['https://polygonscan.com/'],
};

export const RINKEBY = {
  chainId: hexValue(4),
  chainName: 'Rinkeby Test Network',
  rpcUrls: ['https://rinkeby.etherscan.io'],
  nativeCurrency: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
  },
  blockExplorerUrls: ['https://rinkeby.etherscan.io'],
};

export const POLYGON_MUMBAI = {
  chainId: hexValue(80001),
  chainName: 'Mumbai Testnet',
  rpcUrls: ['https://rpc-mumbai.maticvigil.com'],
  nativeCurrency: {
    name: 'MATIC',
    symbol: 'MATIC',
    decimals: 18,
  },
  blockExplorerUrls: ['https://mumbai.polygonscan.com'],
};

export const LOCAL = {
  chainId: hexValue(12345),
  chainName: 'LOCALHOST',
  rpcUrls: ['http://localhost:8545'],
  nativeCurrency: {
    name: 'ETH',
    symbol: 'ETH',
    decimals: 18,
  },
  blockExplorerUrls: [],
};

// note: just for network switching so mainnet eth not needed
export const networks = [POLYGON_MAINNET, RINKEBY, POLYGON_MUMBAI, LOCAL];

export const CHAIN_ID_TO_NETWORK = zipObject(
  networks.map(n => n.chainId),
  networks
);

export const getNetworkDetails = (chainId: string | number): EthereumChainParameter => {
  if (typeof chainId === 'string') {
    return CHAIN_ID_TO_NETWORK[chainId];
  } else {
    return CHAIN_ID_TO_NETWORK[hexValue(chainId)];
  }
};

export const getBlockExploreTxLink = (tx: any, networkId?: number | string) => {
  if (!networkId) return '';

  if (networkId === 1 || networkId === hexValue(1)) {
    return `https://etherscan.io//tx/${tx && tx.hash ? tx.hash : tx}`;
  }

  const networkDetails = getNetworkDetails(networkId);
  if (!networkDetails.blockExplorerUrls || networkDetails.blockExplorerUrls.length === 0) {
    return '';
  }

  return `${networkDetails.blockExplorerUrls[0]}/tx/${tx && tx.hash ? tx.hash : tx}`;
};

export const walletConnectRPCs = {
  1: `https://mainnet.infura.io/v3/${infuraKey}`,
  4: `https://rinkeby.infura.io/v3/${infuraKey}`,
  137: `https://polygon-mainnet.infura.io/v3/${infuraKey}`,
  80001: `https://polygon-mumbai.infura.io/v3/${infuraKey}`,
};

export const mixpassOpenseaURL = isDev()
  ? 'https://testnets.opensea.io/assets/mumbai/'
  : 'https://opensea.io/assets/matic/';

export const chainToOpenSeaBaseUrl = (chain: number) => {
  switch (chain) {
    case 1:
      return 'https://opensea.io/assets';
    case 137:
      return 'https://opensea.io/assets/matic';
    case 80001:
      return 'https://testnets.opensea.io/assets/mumbai';
    case 5:
      return 'https://testnets.opensea.io/assets/goerli';
    default:
      return '';
  }
};

export function getOpenSeaUrl(chain: number, contractAddress: string, tokenId: string) {
  return `${chainToOpenSeaBaseUrl(chain)}/${contractAddress}/${tokenId}`;
}
